import React, { useState, useEffect, useCallback } from 'react';
import { Container, Paper, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from '@mui/material';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Banner from './Banner';
import { useAuth } from '../contexts/AuthContext';

function ActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  }, [logout, navigate]);

  const fetchLogs = useCallback(async () => {
    if (!currentUser) {
      setError('You must be logged in to view logs.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Query the logs collection, ordered by timestamp (newest first)
      const q = query(
        collection(db, 'activityLogs'),
        orderBy('timestamp', 'desc'),
        limit(100) // Limit to the most recent 100 logs
      );

      const querySnapshot = await getDocs(q);
      const logsData = [];

      querySnapshot.forEach((doc) => {
        logsData.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setLogs(logsData);
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError('Failed to fetch activity logs.');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Helper function to format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      // Convert Firestore timestamp to JavaScript Date
      const date = timestamp.toDate();
      return dayjs(date).format('DD-MM-YYYY HH:mm:ss');
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid date';
    }
  };

  // Helper function to format log data for display
  const formatLogData = (log) => {
    if (log.type === 'DELETE') {
      return (
        <>
          <Typography variant="body2" component="div">
            <strong>Vendor:</strong> {log.deletedData?.vendorName || 'N/A'}
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Amount:</strong> ₹{log.deletedData?.amount || 'N/A'}
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Date:</strong> {log.deletedData?.date ? dayjs(log.deletedData.date, 'YYYY-MM-DD').format('DD-MM-YYYY') : 'N/A'}
          </Typography>
        </>
      );
    } else if (log.type === 'EDIT') {
      return (
        <>
          <Typography variant="body2" component="div">
            <strong>Vendor:</strong> {log.oldData?.vendorName || 'N/A'} → {log.newData?.vendorName || 'N/A'}
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Amount:</strong> ₹{log.oldData?.amount || 'N/A'} → ₹{log.newData?.amount || 'N/A'}
          </Typography>
          <Typography variant="body2" component="div">
            <strong>Date:</strong> {log.oldData?.date ? dayjs(log.oldData.date, 'YYYY-MM-DD').format('DD-MM-YYYY') : 'N/A'} → {log.newData?.date ? dayjs(log.newData.date, 'YYYY-MM-DD').format('DD-MM-YYYY') : 'N/A'}
          </Typography>
        </>
      );
    }
    return 'Unknown action';
  };

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      color: '#fff',
    }}>
      <Banner
        title="Activity Logs"
        handleBack={handleBack}
        handleLogout={handleLogout}
      />
      <Container maxWidth="xl" sx={{ flexGrow: 1, py: 4 }}>

      
      {error && (
        <Box sx={{ my: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#2a2a2a', color: '#fff', border: '1px solid #444', borderRadius: 3, boxShadow: 4 }}>
          <Typography variant="h6" gutterBottom>Activity Log</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', borderBottom: '2px solid #9BEC00' }}>Action</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', borderBottom: '2px solid #9BEC00' }}>Details</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', borderBottom: '2px solid #9BEC00' }}>Timestamp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ color: '#aaa', border: 0 }}>
                      No activity logs found.
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log.id} sx={{ '&:hover': { backgroundColor: '#333' } }}>
                      <TableCell sx={{ color: '#fff', borderBottom: '1px solid #444' }}>
                        {log.type === 'DELETE' ? 'Deleted Item' : log.type === 'EDIT' ? 'Edited Item' : 'Unknown Action'}
                      </TableCell>
                      <TableCell sx={{ color: '#fff', borderBottom: '1px solid #444' }}>
                        {formatLogData(log)}
                      </TableCell>
                      <TableCell sx={{ color: '#fff', borderBottom: '1px solid #444' }}>
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        )}
      </Container>
    </Box>
  );
}

export default ActivityLogs;